import { TuiDocument } from '../core/Document';
import { Grid } from '../core/Grid';
import { Selection } from '../core/Selection';
import { Attr, Color, ColorMode, Point, Rect, TRANSPARENT, rectUnion } from '../core/types';
import { quantize } from '../ansi/Palette';

/**
 * Canvas renderer for the editing surface.
 *
 * React never touches a cell: the component owns a canvas, the renderer draws
 * the document into it, and redraws are bounded two ways — only the cells
 * inside the scrolled viewport are considered at all, and within that, only
 * rows marked dirty are repainted. A brush stroke therefore costs a handful of
 * fillText calls rather than a full-tree render.
 */

export interface SafeArea {
  width: number;
  height: number;
  label: string;
  enabled: boolean;
}

export interface RenderOptions {
  fontFamily: string;
  fontSize: number;
  /** Cell aspect: height = width * ratio. 2.0 matches most terminals. */
  cellAspect: number;
  zoom: number;
  showGrid: boolean;
  showRulers: boolean;
  /** Preview quantisation; does not change the document. */
  previewMode: ColorMode;
  background: Color;
  /** Dim everything except the active layer. */
  isolateActiveLayer: boolean;
  safeAreas: SafeArea[];
  showGuides: boolean;
}

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  fontFamily: '"Cascadia Mono", "JetBrains Mono", "DejaVu Sans Mono", Consolas, monospace',
  fontSize: 16,
  cellAspect: 2,
  zoom: 1,
  showGrid: true,
  showRulers: true,
  previewMode: 'truecolor',
  background: 0x06070a,
  isolateActiveLayer: false,
  safeAreas: [
    { width: 80, height: 24, label: '80×24', enabled: false },
    { width: 120, height: 30, label: '120×30', enabled: false },
    { width: 160, height: 50, label: '160×50', enabled: false },
  ],
  showGuides: true,
};

export interface Viewport {
  /** Scroll offset in device-independent pixels. */
  scrollX: number;
  scrollY: number;
  width: number;
  height: number;
}

export const RULER_SIZE = 18;

/** The area around the document. Matches --bg-0 in the stylesheet. */
const OUTSIDE_COLOR = '#0b0c0e';
const GRID_COLOR = 'rgba(255,255,255,0.055)';
const GRID_MAJOR_COLOR = 'rgba(255,255,255,0.11)';
const RULER_BG = '#141518';
const RULER_FG = '#6b7280';
const RULER_ACTIVE = '#d6ff6e';

export interface Overlay {
  selection: Selection | null;
  /** Marching-ants rectangle drawn while dragging a selection or shape. */
  marquee: Rect | null;
  cursor: Point | null;
  /** Cells the in-progress tool would write, drawn as a ghost. */
  preview: Map<number, { char: string; fg: Color; bg: Color; attrs: number }> | null;
  /** Text tool caret. */
  caret: Point | null;
  /** Sub-cell Braille guides for the current cell. */
  brailleCursor: boolean;
}

export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private doc: TuiDocument | null = null;
  private opts: RenderOptions = { ...DEFAULT_RENDER_OPTIONS };
  private overlay: Overlay = {
    selection: null,
    marquee: null,
    cursor: null,
    preview: null,
    caret: null,
    brailleCursor: false,
  };
  private viewport: Viewport = { scrollX: 0, scrollY: 0, width: 0, height: 0 };
  private dirty: Rect | null = null;
  private fullRedraw = true;
  private lastRevision = -1;
  private dpr = 1;
  private frameTimeMs = 0;
  private cellsDrawn = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
  }

  /** Cell width in CSS pixels at the current zoom. */
  get cellWidth(): number {
    return Math.max(1, Math.round(this.opts.fontSize * 0.6 * this.opts.zoom));
  }

  get cellHeight(): number {
    return Math.max(1, Math.round(this.cellWidth * this.opts.cellAspect));
  }

  get stats(): { frameTimeMs: number; cellsDrawn: number } {
    return { frameTimeMs: this.frameTimeMs, cellsDrawn: this.cellsDrawn };
  }

  get rulerOffset(): number {
    return this.opts.showRulers ? RULER_SIZE : 0;
  }

  setDocument(doc: TuiDocument): void {
    if (this.doc !== doc) this.fullRedraw = true;
    this.doc = doc;
  }

  setOptions(opts: Partial<RenderOptions>): void {
    const next = { ...this.opts, ...opts };
    if (JSON.stringify(next) !== JSON.stringify(this.opts)) this.fullRedraw = true;
    this.opts = next;
  }

  getOptions(): RenderOptions {
    return this.opts;
  }

  setOverlay(overlay: Partial<Overlay>): void {
    this.overlay = { ...this.overlay, ...overlay };
    // Overlays are drawn on top of cells, so any change needs a repaint of the
    // area they cover; the cheap, always-correct choice is a full redraw.
    this.fullRedraw = true;
  }

  setViewport(v: Partial<Viewport>): void {
    const next = { ...this.viewport, ...v };
    if (
      next.scrollX !== this.viewport.scrollX ||
      next.scrollY !== this.viewport.scrollY ||
      next.width !== this.viewport.width ||
      next.height !== this.viewport.height
    ) {
      this.fullRedraw = true;
    }
    this.viewport = next;
  }

  markDirty(rect: Rect | null): void {
    if (!rect) return;
    this.dirty = rectUnion(this.dirty, rect);
  }

  markAll(): void {
    this.fullRedraw = true;
  }

  /** Document size in CSS pixels, used for scrollbars. */
  documentSize(): { width: number; height: number } {
    const doc = this.doc;
    if (!doc) return { width: 0, height: 0 };
    return {
      width: doc.width * this.cellWidth + this.rulerOffset,
      height: doc.height * this.cellHeight + this.rulerOffset,
    };
  }

  /**
   * Where the document's top-left corner sits, in CSS pixels.
   *
   * A document smaller than the viewport is centred in the space left over,
   * which is what makes a small design sit in the middle of the editor rather
   * than clinging to the top-left corner. Once it outgrows the viewport the
   * offset is zero and normal scrolling takes over.
   */
  private origin(): { x: number; y: number } {
    const doc = this.doc;
    if (!doc) return { x: this.rulerOffset, y: this.rulerOffset };
    const available = {
      w: this.viewport.width - this.rulerOffset,
      h: this.viewport.height - this.rulerOffset,
    };
    const size = { w: doc.width * this.cellWidth, h: doc.height * this.cellHeight };
    return {
      x: this.rulerOffset + Math.max(0, Math.floor((available.w - size.w) / 2)),
      y: this.rulerOffset + Math.max(0, Math.floor((available.h - size.h) / 2)),
    };
  }

  /** Converts a canvas-relative pixel position to cell + fraction. */
  hitTest(px: number, py: number): { x: number; y: number; fx: number; fy: number } {
    const cw = this.cellWidth;
    const ch = this.cellHeight;
    const origin = this.origin();
    const gx = px + this.viewport.scrollX - origin.x;
    const gy = py + this.viewport.scrollY - origin.y;
    const x = Math.floor(gx / cw);
    const y = Math.floor(gy / ch);
    return {
      x,
      y,
      fx: Math.min(0.999, Math.max(0, gx / cw - x)),
      fy: Math.min(0.999, Math.max(0, gy / ch - y)),
    };
  }

  resize(cssWidth: number, cssHeight: number, dpr = window.devicePixelRatio || 1): void {
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    this.canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    this.canvas.style.width = `${cssWidth}px`;
    this.canvas.style.height = `${cssHeight}px`;
    this.viewport.width = cssWidth;
    this.viewport.height = cssHeight;
    this.fullRedraw = true;
  }

  /** Cell range currently visible, clipped to the document. */
  private visibleRange(): Rect {
    const doc = this.doc;
    if (!doc) return { x: 0, y: 0, width: 0, height: 0 };
    const cw = this.cellWidth;
    const ch = this.cellHeight;
    const origin = this.origin();
    const x0 = Math.max(0, Math.floor((this.viewport.scrollX - origin.x) / cw));
    const y0 = Math.max(0, Math.floor((this.viewport.scrollY - origin.y) / ch));
    const cols = Math.ceil(this.viewport.width / cw) + 1;
    const rows = Math.ceil(this.viewport.height / ch) + 1;
    return {
      x: x0,
      y: y0,
      width: Math.min(doc.width - x0, cols),
      height: Math.min(doc.height - y0, rows),
    };
  }

  render(): void {
    const doc = this.doc;
    if (!doc) return;
    const start = performance.now();
    const ctx = this.ctx;
    const cw = this.cellWidth;
    const ch = this.cellHeight;

    if (doc.revision !== this.lastRevision) {
      this.lastRevision = doc.revision;
      if (!this.dirty) this.fullRedraw = true;
    }

    const visible = this.visibleRange();
    let area: Rect = visible;
    if (!this.fullRedraw && this.dirty) {
      const x0 = Math.max(visible.x, this.dirty.x);
      const y0 = Math.max(visible.y, this.dirty.y);
      const x1 = Math.min(visible.x + visible.width, this.dirty.x + this.dirty.width);
      const y1 = Math.min(visible.y + visible.height, this.dirty.y + this.dirty.height);
      if (x1 <= x0 || y1 <= y0) {
        this.dirty = null;
        return;
      }
      area = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    }

    ctx.save();
    ctx.scale(this.dpr, this.dpr);

    // A full redraw clears the whole surface, not just the cell area. Overlays
    // (marquee, selection edges) can be dragged past the document edge, and the
    // per-cell background fill below never covers the space outside it — so
    // without this those strokes stay on screen as stale pixels.
    if (this.fullRedraw) {
      ctx.fillStyle = OUTSIDE_COLOR;
      ctx.fillRect(0, 0, this.viewport.width, this.viewport.height);
    }

    const origin = this.origin();
    ctx.translate(origin.x - this.viewport.scrollX, origin.y - this.viewport.scrollY);

    // Background for the repainted area.
    ctx.fillStyle = hex(this.opts.background);
    ctx.fillRect(area.x * cw, area.y * ch, area.width * cw, area.height * ch);

    const grid = doc.composite();
    const activeGrid = this.opts.isolateActiveLayer ? doc.activeLayer.grid : null;

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.font = `${this.opts.fontSize * this.opts.zoom}px ${this.opts.fontFamily}`;

    this.cellsDrawn = 0;
    this.drawCells(grid, activeGrid, area, cw, ch);

    if (this.opts.showGrid && cw >= 6) this.drawGrid(area, cw, ch);
    this.drawOverlays(area, cw, ch, grid);
    ctx.restore();

    if (this.opts.showRulers) this.drawRulers(visible, cw, ch);

    this.dirty = null;
    this.fullRedraw = false;
    this.frameTimeMs = performance.now() - start;
  }

  private drawCells(grid: Grid, activeGrid: Grid | null, area: Rect, cw: number, ch: number): void {
    const ctx = this.ctx;
    const preview = this.overlay.preview;
    const mode = this.opts.previewMode;

    for (let y = area.y; y < area.y + area.height; y++) {
      for (let x = area.x; x < area.x + area.width; x++) {
        const idx = y * grid.width + x;
        const ghost = preview?.get(idx);
        const base = grid.get(x, y);
        let char = ghost ? ghost.char : base.char;
        let fg = ghost ? ghost.fg : base.fg;
        let bg = ghost ? ghost.bg : base.bg;
        const attrs = ghost ? ghost.attrs : base.attrs;

        if (mode !== 'truecolor') {
          if (fg !== TRANSPARENT) fg = quantize(fg, mode);
          if (bg !== TRANSPARENT) bg = quantize(bg, mode);
        }
        if ((attrs & Attr.INVERSE) !== 0) {
          const t = fg === TRANSPARENT ? this.opts.background : fg;
          fg = bg === TRANSPARENT ? this.opts.background : bg;
          bg = t;
        }

        const px = x * cw;
        const py = y * ch;
        if (bg !== TRANSPARENT) {
          ctx.fillStyle = hex(bg);
          ctx.fillRect(px, py, cw, ch);
        }

        const dimmed = activeGrid !== null && activeGrid.get(x, y).char === '' && base.char !== '';
        if (char === '' || char === ' ' || (attrs & Attr.HIDDEN) !== 0) {
          if (ghost) this.cellsDrawn += 1;
          continue;
        }

        ctx.globalAlpha = dimmed ? 0.28 : (attrs & Attr.DIM) !== 0 ? 0.6 : ghost ? 0.85 : 1;
        ctx.fillStyle = hex(fg === TRANSPARENT ? 0xc8ccd4 : fg);
        const bold = (attrs & Attr.BOLD) !== 0;
        const italic = (attrs & Attr.ITALIC) !== 0;
        if (bold || italic) {
          ctx.font = `${italic ? 'italic ' : ''}${bold ? '600 ' : ''}${
            this.opts.fontSize * this.opts.zoom
          }px ${this.opts.fontFamily}`;
        }
        ctx.fillText(char, px + cw / 2, py + ch / 2 + 1);
        if (bold || italic) {
          ctx.font = `${this.opts.fontSize * this.opts.zoom}px ${this.opts.fontFamily}`;
        }

        if ((attrs & Attr.UNDERLINE) !== 0) {
          ctx.fillRect(px, py + ch - 2, cw, 1);
        }
        if ((attrs & Attr.STRIKE) !== 0) {
          ctx.fillRect(px, py + ch / 2, cw, 1);
        }
        ctx.globalAlpha = 1;
        this.cellsDrawn += 1;
      }
    }
  }

  private drawGrid(area: Rect, cw: number, ch: number): void {
    const ctx = this.ctx;
    ctx.lineWidth = 1;
    for (let x = area.x; x <= area.x + area.width; x++) {
      ctx.strokeStyle = x % 10 === 0 ? GRID_MAJOR_COLOR : GRID_COLOR;
      ctx.beginPath();
      ctx.moveTo(x * cw + 0.5, area.y * ch);
      ctx.lineTo(x * cw + 0.5, (area.y + area.height) * ch);
      ctx.stroke();
    }
    for (let y = area.y; y <= area.y + area.height; y++) {
      ctx.strokeStyle = y % 5 === 0 ? GRID_MAJOR_COLOR : GRID_COLOR;
      ctx.beginPath();
      ctx.moveTo(area.x * cw, y * ch + 0.5);
      ctx.lineTo((area.x + area.width) * cw, y * ch + 0.5);
      ctx.stroke();
    }
  }

  private drawOverlays(area: Rect, cw: number, ch: number, grid: Grid): void {
    const ctx = this.ctx;
    const { selection, marquee, cursor, caret, brailleCursor } = this.overlay;

    // Everything below is clipped to the document, so a marquee dragged past
    // the edge is cut off at the edge rather than painting over the rulers or
    // the empty space beside the canvas.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, grid.width * cw, grid.height * ch);
    ctx.clip();

    if (selection && !selection.isEmpty) {
      ctx.fillStyle = 'rgba(120, 180, 255, 0.16)';
      ctx.strokeStyle = 'rgba(140, 200, 255, 0.85)';
      ctx.lineWidth = 1;
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!selection.has(x, y)) continue;
          ctx.fillRect(x * cw, y * ch, cw, ch);
          // Outline only the edges of the selected region.
          if (!selection.has(x, y - 1)) edge(ctx, x * cw, y * ch, (x + 1) * cw, y * ch);
          if (!selection.has(x, y + 1)) edge(ctx, x * cw, (y + 1) * ch, (x + 1) * cw, (y + 1) * ch);
          if (!selection.has(x - 1, y)) edge(ctx, x * cw, y * ch, x * cw, (y + 1) * ch);
          if (!selection.has(x + 1, y)) edge(ctx, (x + 1) * cw, y * ch, (x + 1) * cw, (y + 1) * ch);
        }
      }
    }

    for (const safe of this.opts.safeAreas) {
      if (!safe.enabled) continue;
      ctx.strokeStyle = 'rgba(255, 196, 84, 0.55)';
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, safe.width * cw, safe.height * ch);
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255, 196, 84, 0.85)';
      ctx.font = `11px ${this.opts.fontFamily}`;
      ctx.textAlign = 'left';
      ctx.fillText(safe.label, safe.width * cw + 4, safe.height * ch - 6);
      ctx.textAlign = 'center';
      ctx.font = `${this.opts.fontSize * this.opts.zoom}px ${this.opts.fontFamily}`;
    }

    if (this.opts.showGuides && this.doc) {
      ctx.strokeStyle = 'rgba(94, 234, 212, 0.6)';
      for (const g of this.doc.guides) {
        ctx.beginPath();
        if (g.axis === 'x') {
          ctx.moveTo(g.position * cw + 0.5, 0);
          ctx.lineTo(g.position * cw + 0.5, this.doc.height * ch);
        } else {
          ctx.moveTo(0, g.position * ch + 0.5);
          ctx.lineTo(this.doc.width * cw, g.position * ch + 0.5);
        }
        ctx.stroke();
      }
    }

    if (marquee) {
      ctx.strokeStyle = '#8ab4ff';
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.strokeRect(marquee.x * cw + 0.5, marquee.y * ch + 0.5, marquee.width * cw, marquee.height * ch);
      ctx.setLineDash([]);
    }

    if (cursor && grid.inBounds(cursor.x, cursor.y)) {
      ctx.strokeStyle = 'rgba(214, 255, 110, 0.9)';
      ctx.lineWidth = 1;
      ctx.strokeRect(cursor.x * cw + 0.5, cursor.y * ch + 0.5, cw - 1, ch - 1);
      if (brailleCursor) {
        ctx.strokeStyle = 'rgba(214, 255, 110, 0.35)';
        for (let i = 1; i < 2; i++) {
          ctx.beginPath();
          ctx.moveTo(cursor.x * cw + (cw / 2) * i, cursor.y * ch);
          ctx.lineTo(cursor.x * cw + (cw / 2) * i, (cursor.y + 1) * ch);
          ctx.stroke();
        }
        for (let i = 1; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(cursor.x * cw, cursor.y * ch + (ch / 4) * i);
          ctx.lineTo((cursor.x + 1) * cw, cursor.y * ch + (ch / 4) * i);
          ctx.stroke();
        }
      }
    }

    if (caret) {
      ctx.fillStyle = '#d6ff6e';
      ctx.fillRect(caret.x * cw, caret.y * ch, 2, ch);
    }

    ctx.restore();
  }

  private drawRulers(visible: Rect, cw: number, ch: number): void {
    const ctx = this.ctx;
    const cursor = this.overlay.cursor;
    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.fillStyle = RULER_BG;
    ctx.fillRect(0, 0, this.viewport.width, RULER_SIZE);
    ctx.fillRect(0, 0, RULER_SIZE, this.viewport.height);
    ctx.font = '10px ui-monospace, monospace';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const origin = this.origin();
    const ox = origin.x - this.viewport.scrollX;
    const oy = origin.y - this.viewport.scrollY;
    const step = cw < 12 ? 10 : cw < 20 ? 5 : 1;
    for (let x = visible.x; x < visible.x + visible.width; x++) {
      if (x % step !== 0) continue;
      ctx.fillStyle = cursor?.x === x ? RULER_ACTIVE : RULER_FG;
      ctx.fillText(String(x), ox + x * cw + cw / 2, RULER_SIZE / 2);
    }
    const stepY = ch < 12 ? 5 : 1;
    for (let y = visible.y; y < visible.y + visible.height; y++) {
      if (y % stepY !== 0) continue;
      ctx.fillStyle = cursor?.y === y ? RULER_ACTIVE : RULER_FG;
      ctx.fillText(String(y), RULER_SIZE / 2, oy + y * ch + ch / 2);
    }
    ctx.fillStyle = RULER_BG;
    ctx.fillRect(0, 0, RULER_SIZE, RULER_SIZE);
    ctx.restore();
  }
}

function edge(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
  ctx.beginPath();
  ctx.moveTo(x1 + 0.5, y1 + 0.5);
  ctx.lineTo(x2 + 0.5, y2 + 0.5);
  ctx.stroke();
}

export function hex(c: Color): string {
  if (c === TRANSPARENT) return 'rgba(0,0,0,0)';
  return `#${(c & 0xffffff).toString(16).padStart(6, '0')}`;
}

/**
 * The zoom at which a document fits a viewport.
 *
 * Derived from the same expression the renderer uses for cell width, so the
 * answer matches what will actually be drawn. It is rounded *down* to a
 * hundredth: rounding up by a fraction of a percent is enough to push the last
 * column past the edge, which defeats the point of fitting.
 */
export function zoomToFit(
  doc: { width: number; height: number },
  viewport: { width: number; height: number },
  options: { fontSize: number; cellAspect: number; showRulers: boolean; padding?: number },
): number {
  const padding = options.padding ?? 16;
  const offset = options.showRulers ? RULER_SIZE : 0;

  const available = {
    width: Math.max(1, viewport.width - offset - padding),
    height: Math.max(1, viewport.height - offset - padding),
  };
  if (doc.width <= 0 || doc.height <= 0) return 1;

  // cellWidth = fontSize * 0.6 * zoom, and cellHeight = cellWidth * aspect.
  const unitWidth = options.fontSize * 0.6;
  const byWidth = available.width / (doc.width * unitWidth);
  const byHeight = available.height / (doc.height * unitWidth * options.cellAspect);

  const fit = Math.min(byWidth, byHeight);
  return Math.max(0.1, Math.min(6, Math.floor(fit * 100) / 100));
}
